import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

type DurationUnit = 'h' | 'd' | 'w' | 'm';

interface DurationCellProps {
  value: number | null; // Duration in days
  unit?: DurationUnit;
  onChange: (value: number | null, unit?: DurationUnit) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  hoursPerDay?: number;
  daysPerWeek?: number;
  daysPerMonth?: number;
  className?: string;
}

// Conversion factors
const HOURS_PER_DAY = 8;
const DAYS_PER_WEEK = 5;
const DAYS_PER_MONTH = 20;

export function DurationCell({
  value,
  unit = 'd',
  onChange,
  isEditing,
  onStartEdit,
  onEndEdit,
  hoursPerDay = HOURS_PER_DAY,
  daysPerWeek = DAYS_PER_WEEK,
  daysPerMonth = DAYS_PER_MONTH,
  className,
}: DurationCellProps) {
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Convert days to display value based on unit
  const formatDuration = useCallback(
    (days: number | null, displayUnit: DurationUnit): string => {
      if (days === null || days === undefined) return '';

      let displayValue: number;
      switch (displayUnit) {
        case 'h':
          displayValue = days * hoursPerDay;
          break;
        case 'w':
          displayValue = days / daysPerWeek;
          break;
        case 'm':
          displayValue = days / daysPerMonth;
          break;
        case 'd':
        default:
          displayValue = days;
      }

      // Format with appropriate decimal places
      const formatted =
        displayValue % 1 === 0
          ? displayValue.toString()
          : displayValue.toFixed(1);

      return `${formatted}${displayUnit}`;
    },
    [hoursPerDay, daysPerWeek, daysPerMonth]
  );

  // Parse input string like "2d", "4h", "1w", "0.5m"
  const parseDuration = useCallback(
    (str: string): { days: number | null; unit: DurationUnit } => {
      const cleaned = str.trim().toLowerCase();
      if (!cleaned) return { days: null, unit: 'd' };

      // Match number followed by optional unit
      const match = cleaned.match(/^([\d.]+)\s*([hdwm])?$/);
      if (!match) return { days: null, unit: 'd' };

      const numValue = parseFloat(match[1]);
      if (isNaN(numValue)) return { days: null, unit: 'd' };

      const parsedUnit = (match[2] as DurationUnit) || 'd';

      // Convert to days
      let days: number;
      switch (parsedUnit) {
        case 'h':
          days = numValue / hoursPerDay;
          break;
        case 'w':
          days = numValue * daysPerWeek;
          break;
        case 'm':
          days = numValue * daysPerMonth;
          break;
        case 'd':
        default:
          days = numValue;
      }

      return { days, unit: parsedUnit };
    },
    [hoursPerDay, daysPerWeek, daysPerMonth]
  );

  useEffect(() => {
    if (isEditing) {
      setEditValue(formatDuration(value, unit));
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  }, [isEditing, value, unit, formatDuration]);

  const handleSave = useCallback(() => {
    const { days, unit: newUnit } = parseDuration(editValue);
    if (days !== value) {
      onChange(days, newUnit);
    }
    onEndEdit();
  }, [editValue, value, parseDuration, onChange, onEndEdit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEditValue(formatDuration(value, unit));
        onEndEdit();
      } else if (e.key === 'Tab') {
        handleSave();
      }
    },
    [handleSave, onEndEdit, value, unit, formatDuration]
  );

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        placeholder="e.g., 2d, 4h, 1w"
        className={cn(
          'w-full h-full px-2 py-1 text-sm bg-white border-2 border-blue-500 outline-none',
          'rounded-none focus:ring-0',
          className
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        'w-full h-full px-2 py-1 text-sm truncate cursor-text',
        value === null && 'text-gray-400',
        className
      )}
      onDoubleClick={onStartEdit}
    >
      {formatDuration(value, unit)}
    </div>
  );
}

export default DurationCell;
